/**
 * Iter-116 — Unified Flag Panel.
 *
 * Mounted globally inside ProjectHeader. Watches the `?flag={id}`
 * query param. When present:
 *   1. fetches that flag (recipient + sender + admin can read)
 *   2. opens a dialog showing sender, message, timestamp, status,
 *      acknowledgement roster, resolution + reopen history.
 *   3. surfaces all three actions: Acknowledge (personal), Resolve
 *      (global, optional note), Reopen (resolved → active, required
 *      note).
 *
 * The panel doesn't navigate anywhere — clicking a flag card or bell
 * row routes the user to the entity URL with `?flag=` appended,
 * which causes THIS panel to open over the entity content.
 *
 * Closing the panel strips the param so the URL stays clean for
 * sharing.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    AlertTriangle,
    Check,
    Clock,
    Eye,
    History,
    Loader2,
    RotateCcw,
    ShieldCheck,
    Send,
    Inbox as InboxIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import { useAuth } from "../context/AuthContext";
import { formatRelativeTime } from "../lib/media";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";


function entityLabel(t) {
    switch (t) {
        case "state_entry": return "Image";
        case "image_note": return "Image note";
        case "scene": return "Scene";
        case "scene_note": return "Scene note";
        case "character": return "Cast";
        case "character_note": return "Cast note";
        case "supervisor_note": return "Note";
        case "script_note": return "Script note";
        case "continuity_note": return "Continuity";
        case "document": return "Document";
        case "schedule": return "Schedule";
        case "schedule_note": return "Schedule note";
        default: return t || "Item";
    }
}


export default function FlagPanel({ projectId }) {
    const apiBase = useApiBase();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const flagId = useMemo(() => {
        const p = new URLSearchParams(location.search);
        return p.get("flag");
    }, [location.search]);

    const [flag, setFlag] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Action state
    const [acking, setAcking] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolveNote, setResolveNote] = useState("");
    const [resolving, setResolving] = useState(false);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [reopenNote, setReopenNote] = useState("");
    const [reopening, setReopening] = useState(false);

    const closePanel = useCallback(() => {
        const p = new URLSearchParams(location.search);
        p.delete("flag");
        const q = p.toString();
        navigate(`${location.pathname}${q ? `?${q}` : ""}`, { replace: true });
        setFlag(null);
        setError("");
        setResolveOpen(false);
        setReopenOpen(false);
        setResolveNote("");
        setReopenNote("");
    }, [location.pathname, location.search, navigate]);

    const load = useCallback(async () => {
        if (!projectId || !flagId) return;
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(`${apiBase}/${projectId}/flags/${flagId}`);
            setFlag(data);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [projectId, flagId]);

    useEffect(() => {
        if (flagId) load();
        else setFlag(null);
    }, [flagId, load]);

    const acknowledge = async () => {
        if (!flag) return;
        setAcking(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/flags/${flag.id}/acknowledge`
            );
            setFlag(data);
            toast.success("Marked as seen");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setAcking(false);
        }
    };

    const submitResolve = async () => {
        if (!flag || resolving) return;
        setResolving(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/flags/${flag.id}/resolve`,
                { note: resolveNote.trim() || null }
            );
            setFlag(data);
            toast.success("Flag resolved");
            setResolveOpen(false);
            setResolveNote("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setResolving(false);
        }
    };

    const submitReopen = async () => {
        if (!flag || reopening) return;
        const note = reopenNote.trim();
        if (!note) {
            toast.error("Please explain why you're reopening this flag.");
            return;
        }
        setReopening(true);
        try {
            const { data } = await api.post(
                `${apiBase}/${projectId}/flags/${flag.id}/reopen`,
                { note }
            );
            setFlag(data);
            toast.success("Flag reopened");
            setReopenOpen(false);
            setReopenNote("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setReopening(false);
        }
    };

    const isResolved = flag?.status === "resolved";
    const isReopened = flag?.status === "reopened";
    const isActive = flag && (flag.status === "pending" || isReopened);
    const recipientIds = (flag?.recipients || []).map((r) => r.user_id);
    const iAmRecipient = flag
        ? recipientIds.includes(user?.id) || flag.flagged_for === user?.id
        : false;
    const iAmSender = flag?.flagged_by === user?.id;
    const iHaveAcked = (flag?.acknowledged_by || []).includes(user?.id);
    const open = Boolean(flagId);

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o) closePanel();
            }}
        >
            <DialogContent
                data-testid="flag-panel"
                className="max-w-md p-0 overflow-hidden max-h-[90vh] flex flex-col"
            >
                <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5 shrink-0">
                    <DialogTitle className="flex items-center gap-2">
                        <span
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-md ${
                                isResolved
                                    ? "bg-neutral-500/15 text-neutral-400"
                                    : iHaveAcked && iAmRecipient
                                        ? "bg-amber-500/10 text-amber-200"
                                        : "bg-amber-500/20 text-amber-300"
                            }`}
                        >
                            {isResolved ? (
                                <ShieldCheck size={14} />
                            ) : isReopened ? (
                                <RotateCcw size={14} />
                            ) : (
                                <AlertTriangle size={14} />
                            )}
                        </span>
                        Flag
                        <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-normal">
                            {entityLabel(flag?.entity_type)}
                        </span>
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Flag context, acknowledgement and resolution actions.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {loading ? (
                        <p className="text-sm text-neutral-400">Loading…</p>
                    ) : error ? (
                        <p className="text-sm text-red-300">{error}</p>
                    ) : !flag ? null : (
                        <>
                            {/* Status pill */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span
                                    data-testid={`flag-panel-status-${flag.status}`}
                                    className={`text-[10px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded border ${
                                        isResolved
                                            ? "text-emerald-300 bg-emerald-500/8 border-emerald-500/25"
                                            : isReopened
                                                ? "text-amber-300 bg-amber-500/10 border-amber-500/35"
                                                : "text-amber-300 bg-amber-500/8 border-amber-500/30"
                                    }`}
                                >
                                    {flag.status}
                                </span>
                                <span className="text-[10px] text-neutral-500">
                                    {formatRelativeTime(flag.created_at)}
                                </span>
                            </div>

                            {/* Sender + message */}
                            <div className="rounded-lg border border-white/10 bg-card/40 p-3">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                    From
                                </div>
                                <div className="text-sm text-white font-medium">
                                    {flag.flagged_by_name || "—"}
                                </div>
                                {flag.note ? (
                                    <p
                                        data-testid="flag-panel-message"
                                        className="text-sm text-neutral-200 mt-2 whitespace-pre-wrap"
                                    >
                                        {flag.note}
                                    </p>
                                ) : (
                                    <p className="text-sm italic text-neutral-500 mt-2">
                                        (no message)
                                    </p>
                                )}
                            </div>

                            {/* Recipients + ack roster */}
                            {flag.recipients && flag.recipients.length > 0 ? (
                                <div className="rounded-lg border border-white/10 bg-card/40 p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                            Recipients
                                        </div>
                                        {flag.recipient_team ? (
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300">
                                                Whole team
                                            </span>
                                        ) : null}
                                    </div>
                                    <ul
                                        data-testid="flag-panel-recipients"
                                        className="space-y-1.5"
                                    >
                                        {flag.recipients.map((r) => {
                                            const acked = (flag.acknowledged_by || []).includes(r.user_id);
                                            return (
                                                <li
                                                    key={r.user_id}
                                                    data-testid={`flag-panel-recipient-${r.user_id}`}
                                                    className="flex items-center gap-2 text-xs"
                                                >
                                                    <span
                                                        className={`h-5 w-5 rounded-full inline-flex items-center justify-center ${
                                                            acked
                                                                ? "bg-emerald-500/20 text-emerald-300"
                                                                : "bg-white/5 text-neutral-500 border border-white/10"
                                                        }`}
                                                    >
                                                        {acked ? <Check size={10} /> : <Clock size={10} />}
                                                    </span>
                                                    <span className="text-white">
                                                        {r.user_id === user?.id ? "You" : (r.name || "—")}
                                                    </span>
                                                    <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 ml-auto">
                                                        {acked ? "seen" : "pending"}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ) : null}

                            {/* Resolution body */}
                            {isResolved ? (
                                <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/8 p-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/90 mb-1">
                                        Resolved by {flag.resolved_by_name || "—"}
                                    </div>
                                    {flag.resolution_note ? (
                                        <p
                                            data-testid="flag-panel-resolution"
                                            className="text-sm text-neutral-200 whitespace-pre-wrap"
                                        >
                                            {flag.resolution_note}
                                        </p>
                                    ) : (
                                        <p className="text-xs italic text-neutral-400">
                                            (no resolution note)
                                        </p>
                                    )}
                                </div>
                            ) : null}

                            {/* Reopen note */}
                            {isReopened && flag.reopen_note ? (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/90 mb-1">
                                        Reopened by {flag.reopened_by_name || "—"}
                                    </div>
                                    <p
                                        data-testid="flag-panel-reopen-note"
                                        className="text-sm text-neutral-200 whitespace-pre-wrap"
                                    >
                                        {flag.reopen_note}
                                    </p>
                                </div>
                            ) : null}

                            {/* History */}
                            {Array.isArray(flag.reopen_history) && flag.reopen_history.length > 0 ? (
                                <details
                                    data-testid="flag-panel-history"
                                    className="rounded-lg border border-white/10 bg-card/40 p-3"
                                >
                                    <summary className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 cursor-pointer inline-flex items-center gap-1">
                                        <History size={10} />
                                        History ({flag.reopen_history.length})
                                    </summary>
                                    <ul className="mt-2 space-y-2">
                                        {flag.reopen_history.map((h, i) => (
                                            <li
                                                key={i}
                                                className="text-xs text-neutral-300 border-l-2 border-white/10 pl-2"
                                            >
                                                <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                                                    Cycle {h.cycle || i + 1}
                                                </div>
                                                {h.resolution_note ? (
                                                    <p>
                                                        <span className="text-emerald-300/80 mr-1">Resolved</span>
                                                        by {h.resolved_by_name || "—"}: {h.resolution_note}
                                                    </p>
                                                ) : null}
                                                {h.reopen_note ? (
                                                    <p>
                                                        <span className="text-amber-300/90 mr-1">Reopened</span>
                                                        by {h.reopened_by_name || "—"}: {h.reopen_note}
                                                    </p>
                                                ) : null}
                                            </li>
                                        ))}
                                    </ul>
                                </details>
                            ) : null}

                            {/* Actions */}
                            <div className="flex flex-wrap gap-2 pt-1">
                                {isActive && iAmRecipient && !iHaveAcked ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={acknowledge}
                                        disabled={acking}
                                        data-testid="flag-panel-ack"
                                    >
                                        {acking ? (
                                            <Loader2 size={12} className="mr-1.5 animate-spin" />
                                        ) : (
                                            <Eye size={12} className="mr-1.5" />
                                        )}
                                        I&rsquo;ve seen this
                                    </Button>
                                ) : null}
                                {isActive && (iAmRecipient || iAmSender) && !resolveOpen ? (
                                    <Button
                                        size="sm"
                                        onClick={() => setResolveOpen(true)}
                                        data-testid="flag-panel-resolve"
                                    >
                                        <ShieldCheck size={12} className="mr-1.5" />
                                        Resolve
                                    </Button>
                                ) : null}
                                {isResolved && (iAmRecipient || iAmSender) && !reopenOpen ? (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setReopenOpen(true)}
                                        data-testid="flag-panel-reopen"
                                    >
                                        <RotateCcw size={12} className="mr-1.5" />
                                        Reopen
                                    </Button>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>

                {/* Inline resolve form (avoids stacking dialogs) */}
                {resolveOpen ? (
                    <div className="px-5 py-4 border-t border-white/10 bg-card/60 shrink-0 space-y-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Resolve flag for everyone
                        </div>
                        <Textarea
                            data-testid="flag-panel-resolve-note"
                            value={resolveNote}
                            onChange={(e) => setResolveNote(e.target.value)}
                            placeholder="Resolution note (optional)"
                            rows={2}
                            className="text-sm bg-secondary border-white/10"
                        />
                        <div className="flex gap-2 justify-end">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setResolveOpen(false)}
                                disabled={resolving}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={submitResolve}
                                disabled={resolving}
                                data-testid="flag-panel-resolve-submit"
                            >
                                {resolving ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <ShieldCheck size={12} className="mr-1.5" />}
                                Resolve
                            </Button>
                        </div>
                    </div>
                ) : null}

                {/* Inline reopen form */}
                {reopenOpen ? (
                    <div className="px-5 py-4 border-t border-white/10 bg-card/60 shrink-0 space-y-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                            Reopen — note required
                        </div>
                        <Textarea
                            data-testid="flag-panel-reopen-note"
                            value={reopenNote}
                            onChange={(e) => setReopenNote(e.target.value)}
                            placeholder="Why is this not actually resolved?"
                            rows={2}
                            autoFocus
                            className="text-sm bg-secondary border-white/10"
                        />
                        <div className="flex gap-2 justify-end">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setReopenOpen(false)}
                                disabled={reopening}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={submitReopen}
                                disabled={reopening || !reopenNote.trim()}
                                data-testid="flag-panel-reopen-submit"
                            >
                                {reopening ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <RotateCcw size={12} className="mr-1.5" />}
                                Reopen
                            </Button>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
